import { LocalTime } from "@js-joda/core";
import type { LooseValue } from "react-time-picker/dist/cjs/shared/types";
import type {
	MasterLocationDto,
	MasterLocationUpdateDto,
	MyBusinessAvailableAttributesDto,
	MyBusinessInfoUpdateDto,
	MyBusinessLocationAttributeMetadata,
	MyBusinessLocationAttributes,
	MyBusinessLocationAttributesDto,
	MyBusinessLocationCategories,
	MyBusinessLocationUpdateDto,
	MyBusinessSpecialHourPeriod,
	MyBusinessStatefrontAddress,
	MyBusinessTimeOfDay,
	MyBusinessTimePeriod,
} from "shared";
import type { FormMyBusinessLocationFields } from "./FormBusinessLocationFields";
import {
	composePhoneNumber,
	DEFAULT_PHONE_COUNTRY_CODE,
	splitPhoneNumber,
} from "./phoneCountries";

export const BusinnessFormLocationKeyParentMapping: Partial<
	Record<keyof FormMyBusinessLocationFields, keyof MyBusinessLocationUpdateDto>
> = {
	title: "title",
	description: "profile",
	phone: "phoneNumbers",
	phoneCountryCode: "phoneNumbers",
	websiteUri: "websiteUri",
	street: "storefrontAddress",
	regionCode: "storefrontAddress",
	administrativeArea: "storefrontAddress",
	city: "storefrontAddress",
	zip: "storefrontAddress",
	country: "storefrontAddress",
	serviceAreas: "serviceArea",
	businessType: "serviceArea",
	monday: "regularHours",
	tuesday: "regularHours",
	wednesday: "regularHours",
	thursday: "regularHours",
	friday: "regularHours",
	saturday: "regularHours",
	sunday: "regularHours",
	specialHours: "specialHours",
	category: "categories",
	additionalCategories: "categories",
	structuredServices: "serviceItems",
	freeformServices: "serviceItems",
};

const dayOfWeekToFormDay = (
	day: MyBusinessTimePeriod | undefined,
): [MyBusinessTimeOfDay | undefined, MyBusinessTimeOfDay | undefined] => {
	if (!day) {
		return [undefined, undefined];
	}
	return [day.openTime, day.closeTime];
};

function googleDateToInput(
	date:
		| { year?: number | null; month?: number | null; day?: number | null }
		| undefined,
): string | undefined {
	if (!date?.year || !date?.month || !date?.day) return undefined;
	const m = String(date.month).padStart(2, "0");
	const d = String(date.day).padStart(2, "0");
	return `${date.year}-${m}-${d}`;
}

function inputDateToGoogle(date: string | undefined):
	| {
			year: number;
			month: number;
			day: number;
	  }
	| undefined {
	if (!date) return undefined;
	const [y, m, d] = date.split("-").map(Number);
	if (!y || !m || !d) return undefined;
	return { year: y, month: m, day: d };
}

export function locationToFormValues(
	data: MyBusinessLocationUpdateDto,
	attributes: MyBusinessLocationAttributesDto,
): FormMyBusinessLocationFields {
	const phoneParts = splitPhoneNumber(
		data?.phoneNumbers?.primaryPhone,
		DEFAULT_PHONE_COUNTRY_CODE,
		data?.storefrontAddress?.regionCode ?? null,
	);
	return {
		// basic information
		title: data?.title ?? undefined,
		legalName: undefined,
		description: data?.profile?.description ?? undefined,
		phone: phoneParts.phoneNational || undefined,
		phoneCountryCode: phoneParts.phoneCountryCode,
		websiteUri: data?.websiteUri ?? undefined,
		street: data?.storefrontAddress?.addressLines?.[0],
		regionCode: data?.storefrontAddress?.regionCode ?? undefined,
		administrativeArea:
			data?.storefrontAddress?.administrativeArea ?? undefined,
		city: data?.storefrontAddress?.locality ?? undefined,
		zip: data?.storefrontAddress?.postalCode ?? undefined,
		country: data?.storefrontAddress?.regionCode ?? undefined,
		// service area
		serviceAreas: data?.serviceArea?.places?.placeInfos?.map((place) => ({
			place: place.placeName as string,
			placeId: place.placeId as string,
		})),
		businessType:
			(data?.serviceArea
				?.businessType as FormMyBusinessLocationFields["businessType"]) ??
			undefined,
		// business hours
		monday: dayOfWeekToFormDay(
			data?.regularHours?.periods?.find(
				(p) => p.openDay === "MONDAY",
			) as MyBusinessTimePeriod,
		),
		tuesday: dayOfWeekToFormDay(
			data?.regularHours?.periods?.find(
				(p) => p.openDay === "TUESDAY",
			) as MyBusinessTimePeriod,
		),
		wednesday: dayOfWeekToFormDay(
			data?.regularHours?.periods?.find(
				(p) => p.openDay === "WEDNESDAY",
			) as MyBusinessTimePeriod,
		),
		thursday: dayOfWeekToFormDay(
			data?.regularHours?.periods?.find(
				(p) => p.openDay === "THURSDAY",
			) as MyBusinessTimePeriod,
		),
		friday: dayOfWeekToFormDay(
			data?.regularHours?.periods?.find(
				(p) => p.openDay === "FRIDAY",
			) as MyBusinessTimePeriod,
		),
		saturday: dayOfWeekToFormDay(
			data?.regularHours?.periods?.find(
				(p) => p.openDay === "SATURDAY",
			) as MyBusinessTimePeriod,
		),
		sunday: dayOfWeekToFormDay(
			data?.regularHours?.periods?.find(
				(p) => p.openDay === "SUNDAY",
			) as MyBusinessTimePeriod,
		),
		specialHours:
			data?.specialHours?.specialHourPeriods?.map((p, i) => ({
				id: `imported-${googleDateToInput(p.startDate) ?? i}`,
				startDate: googleDateToInput(p.startDate),
				endDate: googleDateToInput(p.endDate),
				closed: !!p.closed,
				openTime: p.openTime ?? undefined,
				closeTime: p.closeTime ?? undefined,
			})) ?? [],
		category: data?.categories?.primaryCategory?.name ?? undefined,
		additionalCategories:
			data?.categories?.additionalCategories?.map(
				(cat) => cat.name as string,
			) ?? undefined,
		// services
		structuredServices:
			data?.serviceItems
				?.filter((item) => item.structuredServiceItem?.serviceTypeId)
				.map((item) => item.structuredServiceItem?.serviceTypeId ?? "") ??
			undefined,
		freeformServices:
			(
				data?.serviceItems?.filter((item) => item.freeFormServiceItem) ?? []
			).map((item) => ({
				name: item.freeFormServiceItem?.label?.displayName ?? "",
				category: item.freeFormServiceItem?.category ?? "",
			})) ?? undefined,
		// attributes
		boolAttributes:
			attributes?.attributes
				?.filter((attr) => attr.valueType === "BOOL")
				.map((attr) => ({ id: attr.name, value: !!attr.values?.[0] })) ??
			undefined,
		urlAttributes:
			attributes?.attributes
				?.filter((attr) => attr?.valueType === "URL")
				.map((attr) => ({
					id: attr.name,
					url: attr.uriValues?.[0]?.uri as string,
				})) ?? undefined,
	};
}

/** Prefer the saved master over the live Google listing when filling the form. */
export function formValuesFromMaster(
	master: MasterLocationDto,
	googleValues: FormMyBusinessLocationFields,
): FormMyBusinessLocationFields {
	const address = master.address;
	const fromMaster = locationToFormValues(
		{
			title: master.publicName,
			profile: { description: master.description },
			phoneNumbers: { primaryPhone: master.phone },
			websiteUri: master.website,
			storefrontAddress: address
				? {
						addressLines:
							address.addressLines ??
							(address.street ? [address.street] : undefined),
						administrativeArea: address.administrativeArea ?? address.state,
						locality: address.locality ?? address.city,
						postalCode: address.postalCode ?? address.zip,
						regionCode: address.regionCode ?? address.country,
					}
				: undefined,
			regularHours: master.hours ?? undefined,
			specialHours: master.specialHours ?? undefined,
			categories: master.categories ?? undefined,
			serviceArea: master.serviceArea ?? undefined,
			serviceItems: master.serviceItems ?? undefined,
		},
		{
			name: "master",
			attributes: master.attributes ?? [],
		},
	);

	return {
		...fromMaster,
		legalName: master.legalName ?? "",
		structuredServices: master.serviceItems
			? fromMaster.structuredServices
			: googleValues.structuredServices,
		freeformServices: master.serviceItems
			? fromMaster.freeformServices
			: googleValues.freeformServices,
		boolAttributes: master.attributes?.length
			? fromMaster.boolAttributes
			: googleValues.boolAttributes,
		urlAttributes: master.attributes?.length
			? fromMaster.urlAttributes
			: googleValues.urlAttributes,
	};
}

/**
 * Maps each editable form field to the Google location FieldMask path(s) it
 * writes to. Attribute fields are omitted — they travel in their own
 * `attributeMask`, not the location mask.
 */
const FormLocationMaskPaths: Partial<
	Record<keyof FormMyBusinessLocationFields, string[]>
> = {
	title: ["title"],
	description: ["profile.description"],
	phone: ["phoneNumbers"],
	phoneCountryCode: ["phoneNumbers"],
	websiteUri: ["websiteUri"],
	street: ["storefrontAddress.addressLines"],
	city: ["storefrontAddress.locality"],
	regionCode: ["storefrontAddress.regionCode"],
	country: ["storefrontAddress.regionCode"],
	administrativeArea: ["storefrontAddress.administrativeArea"],
	zip: ["storefrontAddress.postalCode"],
	serviceAreas: ["serviceArea.places.placeInfos"],
	businessType: ["serviceArea.businessType"],
	monday: ["regularHours.periods"],
	tuesday: ["regularHours.periods"],
	wednesday: ["regularHours.periods"],
	thursday: ["regularHours.periods"],
	friday: ["regularHours.periods"],
	saturday: ["regularHours.periods"],
	sunday: ["regularHours.periods"],
	specialHours: ["specialHours"],
	category: ["categories"],
	additionalCategories: ["categories"],
	structuredServices: ["serviceItems"],
	freeformServices: ["serviceItems"],
};

/**
 * Builds the Google location FieldMask by diffing the form's initial values
 * against the current ones. Only fields the user actually changed are included,
 * so emptying a field clears it on the profile while untouched fields are left
 * intact.
 */
export function buildLocationUpdateMask(
	initialValues: FormMyBusinessLocationFields,
	currentValues: FormMyBusinessLocationFields,
): string[] {
	const paths = new Set<string>();

	for (const [field, maskPaths] of Object.entries(FormLocationMaskPaths) as [
		keyof FormMyBusinessLocationFields,
		string[],
	][]) {
		const changed =
			JSON.stringify(initialValues[field]) !==
			JSON.stringify(currentValues[field]);

		if (changed) {
			for (const path of maskPaths) {
				paths.add(path);
			}
		}
	}

	return Array.from(paths);
}

export function formLocationValuesToDto(
	locationId: string,
	data: FormMyBusinessLocationFields,
): MyBusinessInfoUpdateDto {
	const boolAttributes: MyBusinessLocationAttributes[] = (
		data.boolAttributes ?? []
	).map((attr) => ({
		valueType: "BOOL",
		name: attr.id,
		values: [attr.value],
	}));
	const uriAttributes: MyBusinessLocationAttributes[] = (
		data.urlAttributes ?? []
	)
		.filter((attr) => !!attr?.url?.trim())
		.map((attr) => ({
			valueType: "URL",
			name: attr.id,
			uriValues: [{ uri: attr.url.trim() }],
		}));

	const attributes: MyBusinessLocationAttributesDto = {
		name: `locations/${locationId}/attributes`,
		attributes: [...boolAttributes, ...uriAttributes],
	};
	const structuredServices =
		data.structuredServices?.map((id) => ({
			structuredServiceItem: {
				serviceTypeId: id,
			},
		})) ?? [];

	const freeformServices =
		data.freeformServices?.map((service) => ({
			freeFormServiceItem: {
				label: {
					displayName: service.name,
				},
				category: service.category,
			},
		})) ?? [];

	const hasCategory = !!data.category?.trim();
	const primaryCategory = hasCategory
		? {
				name: data.category?.trim(),
			}
		: undefined;
	const additionalCategories = (data.additionalCategories ?? [])
		.map((cat) => cat.trim())
		.filter(Boolean)
		.map((cat) => ({
			name: cat,
		}));

	const categories: MyBusinessLocationCategories | undefined = hasCategory
		? {
				primaryCategory,
				additionalCategories: additionalCategories.length
					? additionalCategories
					: undefined,
			}
		: undefined;

	const hasAddress = !!(data.street || data.city || data.zip || data.country);

	const storefrontAddress: MyBusinessStatefrontAddress | undefined = hasAddress
		? {
				addressLines: data.street ? [data.street] : undefined,
				locality: data.city,
				postalCode: data.zip,
				administrativeArea: data.administrativeArea,
				regionCode: data.regionCode,
			}
		: undefined;

	const location: MyBusinessLocationUpdateDto = {
		name: locationId,
		title: data.title,
		profile: {
			description: data.description,
		},
		phoneNumbers: {
			primaryPhone: composePhoneNumber(data.phoneCountryCode, data.phone),
		},
		storefrontAddress: storefrontAddress,
		regularHours: {
			periods: [
				{
					openDay: "MONDAY",
					closeDay: "MONDAY",
					openTime: data.monday?.[0],
					closeTime: data.monday?.[1],
				},
				{
					openDay: "TUESDAY",
					closeDay: "TUESDAY",
					openTime: data.tuesday?.[0],
					closeTime: data.tuesday?.[1],
				},
				{
					openDay: "WEDNESDAY",
					closeDay: "WEDNESDAY",
					openTime: data.wednesday?.[0],
					closeTime: data.wednesday?.[1],
				},
				{
					openDay: "THURSDAY",
					closeDay: "THURSDAY",
					openTime: data.thursday?.[0],
					closeTime: data.thursday?.[1],
				},
				{
					openDay: "FRIDAY",
					closeDay: "FRIDAY",
					openTime: data.friday?.[0],
					closeTime: data.friday?.[1],
				},
				{
					openDay: "SATURDAY",
					closeDay: "SATURDAY",
					openTime: data.saturday?.[0],
					closeTime: data.saturday?.[1],
				},
				{
					openDay: "SUNDAY",
					closeDay: "SUNDAY",
					openTime: data.sunday?.[0],
					closeTime: data.sunday?.[1],
				},
			].filter((period) => period.openTime && period.closeTime),
		},
		websiteUri: data.websiteUri,
		categories,
		specialHours: {
			specialHourPeriods: (data.specialHours ?? [])
				.filter((p) => p.startDate)
				.map(
					(p): MyBusinessSpecialHourPeriod => ({
						startDate: inputDateToGoogle(p.startDate),
						endDate: inputDateToGoogle(p.endDate ?? p.startDate),
						closed: !!p.closed,
						openTime: p.closed ? undefined : p.openTime,
						closeTime: p.closed ? undefined : p.closeTime,
					}),
				),
		},
		serviceArea: {
			businessType: data.businessType,
			places: {
				placeInfos: (data?.serviceAreas ?? [])
					.filter((place) => !!place.placeId?.trim())
					.map((place) => {
						const placeId = place.placeId.trim();

						return {
							placeId: placeId.startsWith("places/")
								? placeId.split("/")[1]
								: placeId,
						};
					}),
			},
		},
		serviceItems: [...structuredServices, ...freeformServices].filter(
			(item) => {
				if ("structuredServiceItem" in item) {
					return !!item.structuredServiceItem?.serviceTypeId;
				}
				return !!item.freeFormServiceItem?.label?.displayName?.trim();
			},
		),
	};

	return { attributes, location };
}

/**
 * Convert the Business Manager form into a master-first update payload.
 * Master is the source of truth; Google push is derived server-side.
 */
export function formLocationValuesToMasterUpdateDto(
	data: FormMyBusinessLocationFields,
	updateMask: string[],
): MasterLocationUpdateDto {
	const googleDto = formLocationValuesToDto("unused", data);
	const location = googleDto.location;

	return {
		publicName: location.title ?? null,
		legalName: data.legalName?.trim() ? data.legalName.trim() : null,
		phone: location.phoneNumbers?.primaryPhone ?? null,
		website: location.websiteUri ?? null,
		description: location.profile?.description ?? null,
		address: location.storefrontAddress
			? {
					addressLines: location.storefrontAddress.addressLines ?? null,
					administrativeArea:
						location.storefrontAddress.administrativeArea ?? null,
					locality: location.storefrontAddress.locality ?? null,
					postalCode: location.storefrontAddress.postalCode ?? null,
					regionCode: location.storefrontAddress.regionCode ?? null,
					street: location.storefrontAddress.addressLines?.[0] ?? null,
					city: location.storefrontAddress.locality ?? null,
					state: location.storefrontAddress.administrativeArea ?? null,
					zip: location.storefrontAddress.postalCode ?? null,
					country: location.storefrontAddress.regionCode ?? null,
				}
			: null,
		hours: location.regularHours ?? null,
		specialHours: location.specialHours ?? null,
		categories: location.categories ?? null,
		serviceArea: location.serviceArea ?? null,
		attributes: googleDto.attributes.attributes,
		updateMask,
		serviceItems: location.serviceItems,
	};
}

export function timeOfDayToLocalTime(
	timeOfDay: MyBusinessTimeOfDay | undefined,
): LocalTime | null {
	if (!timeOfDay) {
		return null;
	}
	const hour = (timeOfDay.hours ?? 0) % 24; // When the hours are 24:00, they are the same as 00:00
	const minute = timeOfDay.minutes ?? 0;
	return LocalTime.of(hour, minute);
}

export function LocalTimeToGoogleTime(time: LocalTime): MyBusinessTimeOfDay {
	return {
		hours: time.hour(),
		minutes: time.minute(),
		seconds: time.second(),
		nanos: time.nano(),
	};
}

export function timeOfDayToTimePickerValue(
	timeOfDay: MyBusinessTimeOfDay | undefined,
): LooseValue | undefined {
	if (!timeOfDay) {
		return undefined;
	}

	const hour = (timeOfDay.hours ?? 0).toString().padStart(2, "0");
	const minute = (timeOfDay.minutes ?? 0).toString().padStart(2, "0");

	return `${hour}:${minute}`;
}

export function groupAvailableAttributes<
	T extends keyof MyBusinessLocationAttributeMetadata,
>(attributes: MyBusinessAvailableAttributesDto | undefined, key: T) {
	if (!attributes) {
		return undefined;
	}

	return Object.groupBy(attributes.attributeMetadata, (attribute) =>
		typeof attribute[key] === "string" ? attribute[key] : "Ungrouped",
	);
}
